import { call, put, select, takeLatest } from "redux-saga/effects";

import EmailSubscriptionStatus from "@mapmycustomers/shared/types/email/EmailSubscriptionStatus";
import { EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";
import { ColorPinLegend, ShapePinLegend } from "@mapmycustomers/shared/types/map/PinLegend";
import Organization from "@mapmycustomers/shared/types/Organization";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId, isEmailServiceSupported } from "@app/store/iam";
import { getMapViewSettings } from "@app/store/map";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import type AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import type ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import buildPinLegends from "@app/util/map/buildPinLegends";

import {
  fetchAnnotationData,
  PinLegendsSource,
  subscribeEntityEmail,
  unsubscribeEntityEmail,
} from "./actions";

function* getFetchPayload(
  entityType: MapEntity["entity"],
  entityId: MapEntity["id"],
  pinLegendsSource?: PinLegendsSource
) {
  // Absent prop → default to the map scene's view settings. Scenes with their
  // own view state (e.g. Route) pass pinLegendsSource explicitly.
  const pinLegendKeys: PinLegendsSource = pinLegendsSource ?? (yield select(getMapViewSettings));
  const colorPinLegends: ColorPinLegend[] = yield select(getAllColorLegends);
  const shapePinLegends: ShapePinLegend[] = yield select(getAllShapeLegends);
  const pinLegends = buildPinLegends([entityType], pinLegendKeys, colorPinLegends, shapePinLegends);

  return {
    $filters: {
      cadence: true,
      entities: {
        [entityType]: {
          $and: [{ id: entityId }],
          includeGroups: true,
          includeRoutes: true,
          includeTerritories: true,
        },
      },
      includeAccessStatus: true,
      includeCustomFields: true,
      includeEmailSubscriptionStatus: true,
      includeUsersWithAccess: true,
      pinLegends,
    },
    $limit: 1,
  };
}

export function* onFetchAnnotationPreview({
  payload: { callback, entityId, entityType, failureCallback, pinLegendsSource },
}: ReturnType<typeof fetchAnnotationData>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const emailServiceSupported: boolean = yield select(isEmailServiceSupported);

    const pinsResponse: AggregatedListResponse<MapEntity, ListResponseAggregation<MapEntity>[]> =
      yield callApi(
        "fetchPins",
        orgId,
        yield call(getFetchPayload, entityType, entityId, pinLegendsSource)
      );

    // /pins answers 200 with an empty list for a record that was deleted or is no longer visible, so
    // this is an ordinary outcome rather than a fault — reporting it would show the rep a raw
    // TypeError and fill Sentry with a condition nothing is wrong about.
    const entity: MapEntity | undefined = pinsResponse.data[0];
    if (!entity) {
      yield call(failureCallback);
      return;
    }

    const emailSubscriptionStatus: EmailSubscriptionStatus | undefined =
      emailServiceSupported &&
      (entity.entity === EntityType.COMPANY || entity.entity === EntityType.PERSON) &&
      entity.email
        ? yield callApi("fetchEntityEmailSubscriptionStatus", orgId, entity.entity, entityId)
        : null;

    yield call(callback, {
      emailSubscribed: emailSubscriptionStatus?.subscribed,
      entity,
    });
  } catch (error) {
    // No notification: this is a click-a-pin preview the rep can simply repeat, and the shared saga
    // would title the toast with the raw error message.
    yield put(handleError({ displayNotification: false, error }));
    yield call(failureCallback);
  }
}

export function* onSubscribeEmail({
  payload: { callback, entityId, entityType },
}: ReturnType<typeof subscribeEntityEmail>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("subscribeEntityEmail", orgId, entityType, entityId);
    yield call(callback);
  } catch (error) {
    handleError({ error });
  }
}

export function* onUnsubscribeEmail({
  payload: { callback, entityId, entityType },
}: ReturnType<typeof unsubscribeEntityEmail>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("unsubscribeEntityEmail", orgId, entityType, entityId);
    yield call(callback);
  } catch (error) {
    handleError({ error });
  }
}

export function* entityAnnotationSagas() {
  yield takeLatest(fetchAnnotationData, onFetchAnnotationPreview);
  yield takeLatest(subscribeEntityEmail, onSubscribeEmail);
  yield takeLatest(unsubscribeEntityEmail, onUnsubscribeEmail);
}
